import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import {
  segment,
  withQuery,
  type ArchiveVariables,
  type SaveVariables,
  type Versioned,
} from "@/api/query";

/* Mirrors api/src/pbo_invoicing/vessels/schemas.py. */

export type VesselInput = { name: string; imo: string };

export type Vessel = VesselInput & Versioned & { archived_at: string | null };

export type VesselCallInput = {
  vessel_id: string;
  client_id: string;
  voyage: string;
  terminal: string;
  port: string;
  arrival: string;
  /** When the vessel went alongside, if the client said. Printed on the invoice. */
  berth: string | null;
  departure: string;
  service_from: string;
  service_to: string;
};

export type VesselCall = VesselCallInput &
  Versioned & { vessel_name: string; vessel_imo: string; client_code: string };

export type VesselCallFilters = { clientId?: string; vesselId?: string; limit?: number };

export const vesselKeys = {
  all: ["vessels"] as const,
  lists: ["vessels", "list"] as const,
  list: (q: string, includeArchived: boolean) =>
    ["vessels", "list", { q, includeArchived }] as const,
  calls: ["vessel-calls"] as const,
  callList: (filters: VesselCallFilters) => ["vessel-calls", filters] as const,
};

// --------------------------------------------------------------------------- requests

export function listVessels(q = "", includeArchived = false): Promise<Vessel[]> {
  return apiJson<Vessel[]>(withQuery("/vessels", { q, include_archived: includeArchived }));
}

export function createVessel(input: VesselInput): Promise<Vessel> {
  return apiSend<Vessel>("/vessels", "POST", input);
}

export function getVessel(id: string): Promise<Vessel> {
  return apiJson<Vessel>(`/vessels/${segment(id)}`);
}

export function updateVessel(id: string, input: VesselInput, version: number): Promise<Vessel> {
  return apiSend<Vessel>(`/vessels/${segment(id)}`, "PUT", input, version);
}

export function setVesselArchived(id: string, archived: boolean, version: number): Promise<Vessel> {
  const action = archived ? "archive" : "unarchive";
  return apiSend<Vessel>(`/vessels/${segment(id)}/${action}`, "POST", undefined, version);
}

export function listVesselCalls(filters: VesselCallFilters = {}): Promise<VesselCall[]> {
  return apiJson<VesselCall[]>(
    withQuery("/vessel-calls", {
      client_id: filters.clientId,
      vessel_id: filters.vesselId,
      limit: filters.limit,
    }),
  );
}

export function createVesselCall(input: VesselCallInput): Promise<VesselCall> {
  return apiSend<VesselCall>("/vessel-calls", "POST", input);
}

export function getVesselCall(id: string): Promise<VesselCall> {
  return apiJson<VesselCall>(`/vessel-calls/${segment(id)}`);
}

export function updateVesselCall(
  id: string,
  input: VesselCallInput,
  version: number,
): Promise<VesselCall> {
  return apiSend<VesselCall>(`/vessel-calls/${segment(id)}`, "PUT", input, version);
}

// --------------------------------------------------------------------------- hooks

export function useVessels(q: string, includeArchived: boolean) {
  return useQuery({
    queryKey: vesselKeys.list(q, includeArchived),
    queryFn: () => listVessels(q, includeArchived),
  });
}

export function useSaveVessel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, existing }: SaveVariables<VesselInput>) =>
      existing ? updateVessel(existing.id, input, existing.version) : createVessel(input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: vesselKeys.all }),
        queryClient.invalidateQueries({ queryKey: vesselKeys.calls }),
      ]),
  });
}

export function useArchiveVessel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived, version }: ArchiveVariables) =>
      setVesselArchived(id, archived, version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: vesselKeys.all }),
  });
}

export function useVesselCalls(filters: VesselCallFilters) {
  return useQuery({
    queryKey: vesselKeys.callList(filters),
    queryFn: () => listVesselCalls(filters),
  });
}

export function useSaveVesselCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, existing }: SaveVariables<VesselCallInput>) =>
      existing ? updateVesselCall(existing.id, input, existing.version) : createVesselCall(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: vesselKeys.calls }),
  });
}
